// to be removed
import { appWithTranslation } from "next-i18next";
import { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import ErrorBoundary from "../components/Layout/ErrorBoundary/ErrorBoundary";
import { MainLayout } from "../components/Layout/MainLayout";
import authHOC from "../components/Layout/auth/authHighOrderComponent";
import { AuthProvider } from "../components/Layout/auth/useAuth";
import { Modal } from "../components/global/Modal/ModalComponent";
import { LoadingDotBars } from "../components/global/NewLoading/LoadingDotBars";
import { NewLoading } from "../components/global/NewLoading/NewLoadingComponent";
import BackgroundHooks from "../services/backgroundRequest/BackgroundHooks";
import BackgroundManager from "../services/backgroundRequest/BackgroundManager";
import { useAppSelector } from "../services/hooks/useReduxHooks";
import useRestaurantHydration from "../services/hooks/useRestaurantHydration";
import { persistor, store } from "../services/redux/app/store";
import "../styles/_custom.scss";
import "../styles/globals.scss";
import "../utils/APICalls/AxiosConfig"; // Import Axios configuration here
import { getCookie } from "../utils/CookieHelper";
import LS from "../utils/LocalStorage";
import {
  COOKIE_ENUM,
  LocalStorageEnum,
  PagesEnum,
  SOCKET_NAMESPACE,
  shouldRedirectToOfflineMode,
} from "../utils/enums";
import env from "../utils/env.config";
import { getPageLink } from "../utils/help/navigation/navigation";
import { Page } from "../utils/page";
import {
  getSocket,
  isValidSocketToken,
  logSocketConnectionError,
  refreshSocketAuth,
  resetSocketConnectionErrorLog,
} from "../utils/socket";
import { debugLog } from "../utils/telemetrySanitization";
import CookieCheckPage from "./cookie-check";

const OpenMobileApp = dynamic(
  () =>
    import("../components/appRedirection/appRedirection").then(
      (mod) => mod.OpenMobileApp
    ),
  { ssr: false }
);

const BackgroundRequests = dynamic(
  () => import("../services/backgroundRequest/BackgroundRequests"),
  { ssr: false }
);

interface MyAppProps extends AppProps {
  Component: Page;
  pageProps: any;
}

// Component to handle offline mode redirect
function OfflineModeRedirect() {
  const router = useRouter();
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );

  useEffect(() => {
    // Wait for router to be ready before checking
    if (!router.isReady) {
      return;
    }

    // Check if restaurant has offline mode enabled and no online URL
    if (
      restaurantConfig?.hasOfflineMode &&
      restaurantConfig?.noOnlineUrl === true &&
      shouldRedirectToOfflineMode(router)
    ) {
      router.push(getPageLink(router, PagesEnum.OfflineMode).as);
    }
  }, [restaurantConfig, router.isReady, router.pathname, router]);

  return null;
}

/**
 * Renders nothing — it exists so the hydration hook sits inside the store
 * Provider and PersistGate, where redux-persist has already restored whatever
 * the guest had before. #4166
 */
function RestaurantHydration(props: {
  restaurantInfo?: any;
  socialMediaInfo?: any;
}) {
  useRestaurantHydration(props);

  return null;
}

function MyApp({ Component, pageProps }: MyAppProps) {
  const [loading] = useState(false);

  const router = useRouter();
  const { hasBackButton } = router.query;

  if (hasBackButton) {
    LS._set(LocalStorageEnum.HAS_BACK_BUTTON, true);
  }
  // Wrap the Component with authHOC
  const AuthWrappedComponent = authHOC(Component);

  useEffect(() => {
    const restaurantSocket = getSocket(
      SOCKET_NAMESPACE.RESTAURANT,
      env.restaurantId!
    );
    restaurantSocket.on("connect", () => {
      resetSocketConnectionErrorLog();
      debugLog("Restaurant socket connected");
    });

    let timer: any;
    restaurantSocket.on("connect_error", (error) => {
      logSocketConnectionError(error);
      const token = getCookie(COOKIE_ENUM.TOKEN);
      if (!isValidSocketToken(token)) {
        return;
      }
      timer = setTimeout(() => {
        refreshSocketAuth();
      }, 1000);
    });

    restaurantSocket.on("disconnect", () => {
      debugLog("Restaurant socket disconnected");
    });

    return () => {
      restaurantSocket.off("connect");
      restaurantSocket.off("connect_error");
      clearTimeout(timer);
    };
  }, []); // Empty dependency array ensures this effect runs only once

  useEffect(() => {
    const headElement = document.querySelector("head");
    const config = { attributes: true, childList: true, subtree: true };

    const observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          const build_theme_style = document.querySelector("#theme_style");
          if (build_theme_style) {
            const resId = build_theme_style.getAttribute("data-id");
            const app_theme_style = document.querySelector(`#${resId}`);
            if (app_theme_style && app_theme_style.innerHTML.length > 0) {
              return;
            } else {
              const toBeDeleted =
                document.querySelectorAll("._app_theme_style");
              toBeDeleted.forEach((el) => {
                el.remove();
              });
              const sheet = document.createElement("style");
              sheet.classList.add("_app_theme_style");
              sheet.setAttribute("id", resId!);
              sheet.innerHTML = build_theme_style.innerHTML;
              document.body.appendChild(sheet);
            }
          }
        }
      }
    });

    observer.observe(headElement!, config);
  }, []);

  if (loading) {
    return <></>;
  }

  return LS._checkLocalStorageIfBlocked() ? (
    <CookieCheckPage />
  ) : !loading ? (
    <>
      <ErrorBoundary>
        <Provider store={store}>
          <PersistGate loading={<LoadingDotBars />} persistor={persistor}>
            <AuthProvider>
              {/* Rendered ahead of BackgroundManager on purpose: sibling
                  effects fire in order, so the config from the page props is
                  in redux before BackgroundHooks decides whether it still
                  needs to request anything. */}
              <RestaurantHydration
                restaurantInfo={pageProps.restaurantInfo}
                socialMediaInfo={pageProps.socialMediaInfo}
              />

              <BackgroundManager>
                <BackgroundHooks />
                <BackgroundRequests />
              </BackgroundManager>

              <OfflineModeRedirect />
              <NewLoading />
              <MainLayout>
                <AuthWrappedComponent {...pageProps} />
                <Modal />
                <OpenMobileApp />
              </MainLayout>
            </AuthProvider>
          </PersistGate>
        </Provider>
      </ErrorBoundary>
    </>
  ) : (
    <></>
  );
}

export default appWithTranslation(MyApp);
